import { useEffect, useRef, useState } from "react";
import { Alert, App, Button, Form, Input, Modal, Spin, Tag, type InputRef } from "antd";
import type { ItemStatus } from "@shoe-trace/shared";
import { ITEM_STATUS_COLOR, unitStatusText } from "@shoe-trace/shared";
import { useSession } from "../session";

export function StatusTag({
  status,
  locationCode,
}: {
  status: ItemStatus;
  locationCode?: string | null;
}): JSX.Element {
  return <Tag color={ITEM_STATUS_COLOR[status]}>{unitStatusText(status, locationCode)}</Tag>;
}

export function formatWhen(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}

export function formatDay(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("zh-CN");
}

export function LoadFail({
  title,
  onRetry,
}: {
  title: string;
  onRetry: () => void;
}): JSX.Element {
  return (
    <Alert
      type="error"
      showIcon
      message={title}
      description="网络可能不稳定，或系统没连上。请看右上角状态，连上后再试一次。"
      action={<Button onClick={onRetry}>再读一次</Button>}
    />
  );
}

export function PageSpin({ tip }: { tip: string }): JSX.Element {
  return (
    <Spin spinning tip={tip}>
      <div style={{ minHeight: 240 }} />
    </Spin>
  );
}

export function AdminScanNotice({ compact = false }: { compact?: boolean }): JSX.Element | null {
  const { user } = useSession();
  if (user?.role !== "ADMIN") return null;
  if (compact) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 8 }}
        message="管理员账号请改用作业员自己的工号来扫码。"
      />
    );
  }
  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 16 }}
      message="你正在用管理员账号"
      description="这个账号用来开通和停用人员。流水线上的扫码请让作业员用自己的工号登录，这样每双鞋能记到具体的人。"
    />
  );
}

export function ScanBox({
  placeholder,
  disabled,
  focusToken = 0,
  onSubmit,
}: {
  placeholder: string;
  disabled?: boolean;
  focusToken?: number;
  onSubmit: (value: string) => Promise<void>;
}): JSX.Element {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef<InputRef>(null);
  const busyRef = useRef(false);

  useEffect(() => {
    if (disabled) return;
    ref.current?.focus({ preventScroll: true });
  }, [focusToken, disabled]);

  return (
    <div className="scan-box-wrap">
      <Input
        ref={ref}
        className="scan-box"
        size="large"
        value={value}
        disabled={disabled || busy}
        placeholder={placeholder}
        onChange={(event) => setValue(event.target.value)}
        onPressEnter={(event) => {
          const next = event.currentTarget.value.trim();
          if (!next || busyRef.current) return;
          busyRef.current = true;
          setBusy(true);
          void onSubmit(next)
            .catch(() => undefined)
            .finally(() => {
              busyRef.current = false;
              setValue("");
              setBusy(false);
              window.setTimeout(() => ref.current?.focus({ preventScroll: true }), 0);
            });
        }}
      />
    </div>
  );
}

export function PasswordModal({
  open,
  okText,
  note,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  okText: string;
  note?: string;
  onCancel: () => void;
  onConfirm: (password: string) => Promise<void>;
}): JSX.Element {
  const { message } = App.useApp();
  const [form] = Form.useForm<{ password: string }>();

  return (
    <Modal
      centered
      destroyOnClose
      maskClosable={false}
      open={open}
      title="确认是你本人在操作"
      okText={okText}
      cancelText="先不操作"
      onCancel={onCancel}
      afterClose={() => form.resetFields()}
      onOk={() => {
        const password = String(form.getFieldValue("password") ?? "");
        if (!password.trim()) {
          message.warning("密码还空着。请输入你的登录密码后再继续。");
          return Promise.reject(new Error("empty"));
        }
        return onConfirm(password).then(() => {
          form.resetFields();
          window.setTimeout(onCancel, 0);
        }).catch((error: unknown) => {
          const text = error instanceof Error ? error.message : "密码不对，这一步没有执行。请重输；忘了就让管理员重置密码。";
          message.error(text);
          return Promise.reject(error instanceof Error ? error : new Error(text));
        });
      }}
    >
      {note ? <Alert style={{ marginBottom: 12 }} type="info" showIcon message={note} /> : null}
      <Form form={form} layout="vertical" requiredMark>
        <Form.Item label="请再输入一次你的密码" name="password">
          <Input.Password autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function requestStatusLabel(status: string): string {
  if (status === "PENDING") return "待处理";
  if (status === "APPROVED") return "已通过";
  if (status === "REJECTED") return "已退回";
  if (status === "NOTED") return "已记下";
  return status;
}
